import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from 'react';
import { Box, Button, Row, TextField, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { DisplayScopeCheckbox } from '~/components/DisplayScopeCheckbox/DisplayScopeCheckbox';
import { UploadedPdfRow } from '~/components/UploadedPdfRow/UploadedPdfRow';
import { useStore } from '~/contexts';
import { useCreateCustomPageTemplate } from '~/hooks/reactQuery/mutations/useCreateCustomPageTemplate';
import { useTemplateSaveErrorHandler } from '~/hooks/reactQuery/mutations/useTemplateSaveErrorHandler';
import { useUpdateCustomPageTemplate } from '~/hooks/reactQuery/mutations/useUpdateCustomPageTemplate';
import { useCustomPageTemplates } from '~/hooks/reactQuery/queries/useCustomPageTemplates';
import { useFilePicker } from '~/hooks/useFilePicker';
import { usePreviewCustomPagePdf } from '~/hooks/usePreviewCustomPagePdf';
import { useUploadCustomPagePdf } from '~/hooks/useUploadCustomPagePdf';
import { useLocaleKeys } from '~/i18n';
import { isDuplicateTemplateName } from '~/models/custom-page-template/isDuplicateTemplateName';
import { selectors } from '~/store/state-selectors';
import s from './PdfTemplateFormModal.module.scss';
export const GetPdfTemplateFormModalTID = (parentTestId)=>getTestIds(parentTestId);
export const PdfTemplateForm = (param)=>{
    let { mode, template, initialUploadedFile, onBack, onSaved } = param;
    var _ref;
    const { t } = useLocaleKeys();
    const testIds = useMemo(()=>GetPdfTemplateFormModalTID(), []);
    const businessId = useStore(selectors.user.businessId);
    const { templates: existingTemplates } = useCustomPageTemplates();
    const [name, setName] = useState(mode === 'edit' && template ? template.name : '');
    const [pageTitle, setPageTitle] = useState(mode === 'edit' && template ? template.pageTitle : '');
    const [pdfFile, setPdfFile] = useState(()=>{
        if (mode === 'edit' && template) {
            return {
                pdfName: template.pdf.pdfName,
                pdfFileName: template.pdf.pdfFileName
            };
        }
        return initialUploadedFile;
    });
    const [displayScope, setDisplayScope] = useState(mode === 'edit' && template ? template.displayScope : 'proposalOnly');
    const [nameError, setNameError] = useState(undefined);
    const [pageTitleError, setPageTitleError] = useState(undefined);
    const [fileError, setFileError] = useState(undefined);
    const { createTemplate, isLoading: isCreateLoading } = useCreateCustomPageTemplate({
        onSuccess: ()=>{
            onSaved();
        }
    });
    const { updateTemplate, isLoading: isUpdateLoading } = useUpdateCustomPageTemplate({
        onSuccess: ()=>{
            onSaved();
        }
    });
    const handleSaveError = useTemplateSaveErrorHandler({
        mode,
        setNameError
    });
    const isLoading = isCreateLoading || isUpdateLoading;
    const handleUploadSuccess = useCallback((fileInfo)=>{
        setPdfFile(fileInfo);
        setFileError(undefined);
    }, []);
    const { handleFileSelected, isUploading } = useUploadCustomPagePdf({
        onSuccess: handleUploadSuccess,
        fileSizeErrorTestId: testIds.PdfTemplateFormModalTestIds.FileSizeErrorToast(),
        uploadErrorTestId: testIds.PdfTemplateFormModalTestIds.UploadErrorToast()
    });
    const { openFilePicker, fileInputElement } = useFilePicker({
        accept: '.pdf',
        onFileSelected: handleFileSelected,
        testId: testIds.PdfTemplateFormModalTestIds.FileInput()
    });
    const { openPreview } = usePreviewCustomPagePdf({
        biName: 'pdf_template_form_preview_modal'
    });
    const handlePreview = useCallback(()=>{
        if (pdfFile == null) return;
        openPreview(pdfFile.pdfName);
    }, [
        pdfFile,
        openPreview
    ]);
    const handleSave = ()=>{
        if (isLoading || isUploading) return;
        let hasError = false;
        if (name.trim() === '') {
            setNameError(t.customPages.templates.fieldRequired());
            hasError = true;
        } else if (isDuplicateTemplateName(name, existingTemplates, template === null || template === void 0 ? void 0 : template.id)) {
            setNameError(t.customPages.templates.duplicateNameMessage());
            hasError = true;
        } else {
            setNameError(undefined);
        }
        if (pageTitle.trim() === '') {
            setPageTitleError(t.customPages.templates.fieldRequired());
            hasError = true;
        } else {
            setPageTitleError(undefined);
        }
        if (pdfFile == null) {
            setFileError(t.customPages.templates.fileUploadRequired());
            hasError = true;
        } else {
            setFileError(undefined);
        }
        if (hasError) return;
        const payload = {
            type: 'pdf',
            name: name.trim(),
            pageTitle: pageTitle.trim(),
            displayScope,
            pdf: {
                pdfName: pdfFile.pdfName,
                pdfFileName: pdfFile.pdfFileName
            }
        };
        if (mode === 'create') {
            createTemplate({
                businessId: businessId !== null && businessId !== void 0 ? businessId : '',
                data: payload
            }, {
                onError: handleSaveError
            });
        } else if (template) {
            updateTemplate({
                businessId: businessId !== null && businessId !== void 0 ? businessId : '',
                templateId: template.id,
                data: payload
            }, {
                onError: handleSaveError
            });
        }
    };
    const handleNameChange = (e)=>{
        setName(e.target.value);
        if (nameError) {
            setNameError(undefined);
        }
    };
    const handlePageTitleChange = (e)=>{
        setPageTitle(e.target.value);
        if (pageTitleError) {
            setPageTitleError(undefined);
        }
    };
    const handleCheckboxChange = ()=>{
        setDisplayScope((prev)=>prev === 'proposalOnly' ? 'proposalAndAgreement' : 'proposalOnly');
    };
    const hasFile = pdfFile != null || isUploading;
    return /*#__PURE__*/ _jsxs(Box, {
        testId: testIds.PdfTemplateFormModalTestIds.Root(),
        className: s.formContent,
        children: [
            /*#__PURE__*/ _jsx(TextField, {
                testId: testIds.PdfTemplateFormModalTestIds.NameField(),
                label: t.customPages.templates.nameLabel(),
                value: name,
                onChange: handleNameChange,
                error: nameError != null,
                helperText: nameError,
                bi: {
                    name: 'pdf_template_form_name'
                }
            }),
            /*#__PURE__*/ _jsx(TextField, {
                testId: testIds.PdfTemplateFormModalTestIds.PageTitleField(),
                label: t.customPages.templates.pageTitleLabel(),
                value: pageTitle,
                onChange: handlePageTitleChange,
                error: pageTitleError != null,
                helperText: pageTitleError,
                bi: {
                    name: 'pdf_template_form_page_title'
                }
            }),
            fileInputElement,
            hasFile ? /*#__PURE__*/ _jsx(UploadedPdfRow, {
                testId: testIds.PdfTemplateFormModalTestIds.UploadedPdfRow(),
                pdfFileName: (_ref = pdfFile === null || pdfFile === void 0 ? void 0 : pdfFile.pdfFileName) !== null && _ref !== void 0 ? _ref : '',
                fileSizeBytes: pdfFile === null || pdfFile === void 0 ? void 0 : pdfFile.fileSizeBytes,
                onChangeFile: openFilePicker,
                onPreview: handlePreview,
                isUploading: isUploading
            }) : /*#__PURE__*/ _jsxs(Box, {
                children: [
                    /*#__PURE__*/ _jsxs(Box, {
                        testId: testIds.PdfTemplateFormModalTestIds.UploadArea(),
                        className: "".concat(s.uploadArea).concat(fileError != null ? " ".concat(s.hasError) : ''),
                        onClick: openFilePicker,
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph2",
                                children: t.customPages.pdfUpload.dragOrClick()
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph3",
                                skin: "secondary",
                                children: t.customPages.pdfUpload.sizeLimit()
                            })
                        ]
                    }),
                    fileError != null && /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph3",
                        className: s.uploadErrorText,
                        testId: testIds.PdfTemplateFormModalTestIds.UploadAreaError(),
                        children: fileError
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(DisplayScopeCheckbox, {
                testId: testIds.PdfTemplateFormModalTestIds.DisplayScopeCheckbox(),
                checked: displayScope === 'proposalAndAgreement',
                onChange: handleCheckboxChange
            }),
            /*#__PURE__*/ _jsxs(Row, {
                className: s.formFooter,
                justifyContent: "center",
                gap: 12,
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        skin: "primaryOutline",
                        onClick: onBack,
                        testId: testIds.PdfTemplateFormModalTestIds.CancelButton(),
                        bi: {
                            name: 'pdf_template_form_cancel'
                        },
                        children: t.customPages.templates.cancel()
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        skin: "primary",
                        loading: isLoading || isUploading,
                        onClick: handleSave,
                        testId: testIds.PdfTemplateFormModalTestIds.SaveButton(),
                        bi: {
                            name: 'pdf_template_form_save'
                        },
                        children: t.customPages.templates.save()
                    })
                ]
            })
        ]
    });
};
